import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { clearSettingsCache } from '../services/settingsService';
import { isHiddenModule } from '../config/hiddenModules';
import {
  getMasterSession,
  setMasterSession,
  getStaffSession,
  clearInvalidAuthStorage,
  isJwtExpired,
} from '../utils/session';

const AuthContext = createContext();

// Maps an employee's granular permission flags → the concrete sidebar module ids
// they are allowed to see. Keeps staff access strictly within their permissions.
const STAFF_PERM_MODULE_MAP = {
  // 1:1 module permissions (each maps to its own sidebar id).
  dashboard: ['dashboard'],
  clients: ['clients'],
  cars: ['cars'],
  rentals: ['rentals'],
  reservations: ['reservations'],
  calendar: ['calendar'],
  finances: ['finances'],
  invoices: ['invoices'],
  cheques: ['cheques'],
  mokhalafat: ['mokhalafat'],
  staff: ['staff'],
  gps: ['gps'],
  breakdowns: ['breakdowns'],
  history: ['history'],
  blacklistcheck: ['blacklistcheck'],
  referral: ['referral'],
  analytics: ['analytics'],
  vidange: ['vidange'],
  settings: ['settings'],
  // Legacy grouped keys kept for backward compatibility with older staff records.
  admin: ['settings', 'staff', 'history', 'mokhalafat'],
};
export function permsToModules(permissions = {}) {
  const set = new Set(['dashboard']);
  Object.keys(permissions || {}).forEach((k) => {
    if (permissions[k] && STAFF_PERM_MODULE_MAP[k]) STAFF_PERM_MODULE_MAP[k].forEach((m) => set.add(m));
  });
  // Never surface globally-hidden modules, even to staff with the permission.
  return Array.from(set).filter((m) => !isHiddenModule(m));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agencyId, setAgencyId] = useState(null);
  const [enabledModules, setEnabledModules] = useState(null);
  const [enabledApis, setEnabledApis] = useState(null);
  const [isStaff, setIsStaff] = useState(false);

  // Check URL query parameters for special SPA modes
  const [signatureMode, setSignatureMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('sign') || null;
  });

  const [reserveMode, setReserveMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('reserve');
  });

  const [reserveAgencyId, setReserveAgencyId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('agency') || null;
  });

  const [superAdminMode, setSuperAdminMode] = useState(() => {
    return window.__IS_SUPER_ADMIN__ === true || window.location.pathname === '/superadmin';
  });

  const [impersonateMode, setImpersonateMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('impersonate') || null;
  });

  const resolvingUserIdRef = useRef(null);

  useEffect(() => {
    console.log('[AUTH] Extension Startup: Initializing AuthProvider', {
      userAgent: navigator.userAgent,
      time: new Date().toISOString(),
    });

    let isMounted = true;
    let initTimer = null;

    const finishLoading = (userObj = null, agency = null) => {
      if (!isMounted) return;
      if (initTimer) clearTimeout(initTimer);
      setUser(userObj);
      setAgencyId(agency);
      window.__AGENCY_ID__ = agency;
      setLoading(false);
      console.log('[AUTH] Initialization Completed', {
        user: userObj ? (userObj.email || userObj.uid || userObj.username) : 'None (Login View)',
        agencyId: agency,
      });
    };

    // Maximum loading time: 10 seconds. Guarantees the loading screen never stays forever.
    initTimer = setTimeout(() => {
      if (isMounted && loading) {
        console.warn('[AUTH] Initialization Timeout reached (10s). Safely recovering session state...');
        clearInvalidAuthStorage();
        finishLoading(null, null);
      }
    }, 10000);

    const resolveUserSession = async (sessionObj) => {
      if (!sessionObj || !sessionObj.user) {
        finishLoading(null, null);
        return;
      }

      const u = sessionObj.user;
      if (resolvingUserIdRef.current === u.id) {
        console.log('[AUTH] resolveUserSession already in progress for uid:', u.id);
        return;
      }
      resolvingUserIdRef.current = u.id;

      try {
        // Check access token expiry
        if (sessionObj.access_token && isJwtExpired(sessionObj.access_token)) {
          console.log('[AUTH] Token Expired: access token expired, initiating refresh...');
          const { data: refreshData, error: refreshErr } = await supabase.auth.refreshSession();
          if (refreshErr || !refreshData.session) {
            console.error('[AUTH] Refresh Failed:', refreshErr?.message || 'No refreshed session available');
            clearInvalidAuthStorage();
            finishLoading(null, null);
            return;
          }
          sessionObj = refreshData.session;
        }

        let reg = null;
        try {
          const regPromise = supabase
            .from('user_registrations')
            .select('*')
            .eq('uid', u.id)
            .single();

          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('user_registrations lookup timeout (5s)')), 5000)
          );

          const res = await Promise.race([regPromise, timeoutPromise]);
          reg = res.data;
        } catch (dbErr) {
          console.warn('[AUTH] user_registrations lookup timeout/error:', dbErr.message);
        }

        if (!isMounted) return;

        if (reg) {
          if (reg.status === 'pending' || reg.status === 'rejected') {
            console.log('[AUTH] Account status is pending/rejected. Signing out...');
            await supabase.auth.signOut().catch(() => {});
            clearInvalidAuthStorage();
            finishLoading(null, null);
            return;
          }
          // Approved agency account
          const aId = reg.agency_id || u.id;
          const validMods = (Array.isArray(reg.enabled_modules) && reg.enabled_modules.length > 0) ? reg.enabled_modules : null;
          setEnabledModules(validMods);
          window.__ENABLED_MODULES__ = validMods;
          setEnabledApis(reg.enabled_apis || null);
          window.__ENABLED_APIS__ = reg.enabled_apis || null;

          // Log Login History / Activity in data.loginHistory
          try {
            const currentHistory = reg.data?.loginHistory || [];
            const newLogin = {
              timestamp: new Date().toISOString(),
              userAgent: navigator.userAgent
            };
            const lastLog = currentHistory[0];
            const tenMin = 10 * 60 * 1000;
            if (!lastLog || (Date.now() - new Date(lastLog.timestamp).getTime()) > tenMin) {
              const updatedData = {
                ...(reg.data || {}),
                lastLoginAt: newLogin.timestamp,
                loginHistory: [newLogin, ...currentHistory].slice(0, 10)
              };
              supabase.from('user_registrations').update({ data: updatedData }).eq('id', reg.id).then(() => {});
            }
          } catch (_) {}

          console.log('[AUTH] Session Restored: Approved Supabase user', u.email || u.id, 'agencyId:', aId);
          finishLoading(u, aId);
        } else {
          console.log('[AUTH] Session Restored: Supabase user (default agency ID)', u.email || u.id);
          finishLoading(u, u.id);
        }
      } catch (err) {
        console.error('[AUTH] Uncaught error during resolveUserSession:', err);
        clearInvalidAuthStorage();
        finishLoading(null, null);
      } finally {
        resolvingUserIdRef.current = null;
      }
    };

    const initAuth = async () => {
      // 0. Check Impersonation Mode first
      if (impersonateMode) {
        try {
          console.log('[AUTH] Checking impersonation mode for uid:', impersonateMode);
          const { data: reg } = await supabase
            .from('user_registrations')
            .select('*')
            .eq('uid', impersonateMode)
            .single();

          if (reg) {
            const mockUser = {
              id: reg.uid,
              uid: reg.uid,
              email: reg.email || reg.uid,
              displayName: reg.data?.agencyName || reg.data?.name || reg.uid,
              isImpersonated: true
            };
            const aId = reg.agency_id || reg.uid;
            const validMods = (Array.isArray(reg.enabled_modules) && reg.enabled_modules.length > 0) ? reg.enabled_modules : null;
            setEnabledModules(validMods);
            window.__ENABLED_MODULES__ = validMods;
            setEnabledApis(reg.enabled_apis || null);
            window.__ENABLED_APIS__ = reg.enabled_apis || null;
            console.log('[AUTH] Session Restored: Impersonated session', mockUser.email);
            finishLoading(mockUser, aId);
            return;
          }
        } catch (e) {
          console.error('[AUTH] Error setting up impersonated session:', e);
        }
      }

      // 1. Check if user is logged in via Master Password
      const masterUser = getMasterSession();
      if (masterUser) {
        console.log('[AUTH] Session Restored: Master password user', masterUser.email);
        finishLoading(masterUser, masterUser.uid);
        return;
      }

      // 1b. Employee (staff) session — agency-scoped, permission-gated
      const staffUser = getStaffSession();
      if (staffUser) {
        console.log('[AUTH] Session Restored: Staff employee session', staffUser.username);
        const mods = permsToModules(staffUser.permissions);
        const u = { ...staffUser, isStaff: true, displayName: staffUser.name || staffUser.username, uid: `staff-${staffUser.staffId}` };
        setIsStaff(true);
        setEnabledModules(mods);
        window.__ENABLED_MODULES__ = mods;
        window.__STAFF_PERMISSIONS__ = staffUser.permissions || {};
        finishLoading(u, staffUser.agencyId);
        return;
      }

      // 2. Supabase Auth Session Restoration
      try {
        console.log('[AUTH] Storage Read: Checking Supabase auth getSession()');
        const { data: { session }, error: sessionErr } = await supabase.auth.getSession();

        if (sessionErr) {
          console.warn('[AUTH] Supabase getSession error:', sessionErr.message);
          if (sessionErr.message?.includes('invalid') || sessionErr.message?.includes('expired')) {
            console.log('[AUTH] Token Expired / Invalid session returned');
            clearInvalidAuthStorage();
          }
        }

        if (session && session.user) {
          await resolveUserSession(session);
        } else {
          console.log('[AUTH] Session Missing: No active session found');
          finishLoading(null, null);
        }
      } catch (err) {
        console.error('[AUTH] Uncaught error during auth initialization:', err);
        clearInvalidAuthStorage();
        finishLoading(null, null);
      }
    };

    initAuth();

    // Listen to background auth state changes (e.g. multi-tab signouts or token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AUTH] Supabase AuthStateChange:', event);

      if (event === 'SIGNED_OUT') {
        console.log('[AUTH] Logout: Received SIGNED_OUT event from Supabase');
        clearInvalidAuthStorage();
        if (isMounted) {
          setUser(null);
          setAgencyId(null);
          window.__AGENCY_ID__ = null;
          setEnabledModules(null);
          window.__ENABLED_MODULES__ = null;
          setEnabledApis(null);
          window.__ENABLED_APIS__ = null;
          setIsStaff(false);
          setLoading(false);
        }
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        console.log('[AUTH] Refresh Succeeded: Access token auto-refreshed');
      }

      if (session && session.user && isMounted) {
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || !window.__AGENCY_ID__) {
          await resolveUserSession(session);
        } else {
          setUser(session.user);
          setLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      if (initTimer) clearTimeout(initTimer);
      subscription.unsubscribe();
    };
  }, [impersonateMode]);

  const loginWithMasterPassword = (email, userObj) => {
    console.log('[AUTH] Storage Write: Login with Master Password', email);
    setMasterSession(userObj);
    setUser(userObj);
    setAgencyId(userObj.uid);
    window.__AGENCY_ID__ = userObj.uid;
  };

  // Employee login: `session` comes from /api/staff/login (token + agency + perms).
  const loginWithStaff = (session) => {
    console.log('[AUTH] Storage Write: Login with Staff Account', session.username);
    localStorage.setItem('staffUser', JSON.stringify(session));
    const mods = permsToModules(session.permissions);
    setUser({ ...session, isStaff: true, displayName: session.name || session.username, uid: `staff-${session.staffId}` });
    setIsStaff(true);
    setAgencyId(session.agencyId);
    window.__AGENCY_ID__ = session.agencyId;
    setEnabledModules(mods);
    window.__ENABLED_MODULES__ = mods;
    window.__STAFF_PERMISSIONS__ = session.permissions || {};
  };

  const logout = async () => {
    console.log('[AUTH] Logout initiated');
    clearInvalidAuthStorage();
    setIsStaff(false);
    clearSettingsCache(); // wipe cached settings so the next agency starts clean
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error('[AUTH] Sign out error:', e);
    }
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      agencyId,
      enabledModules,
      enabledApis,
      isStaff,
      signatureMode,
      reserveMode,
      reserveAgencyId,
      superAdminMode,
      loginWithMasterPassword,
      loginWithStaff,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
